import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { Form, Spinner, Alert } from "reactstrap";
import { ReactTyped } from "react-typed";
import axios from "axios";

import Head from "../layout/head/Head";
import AuthFooter from "./auth/AuthFooter";
import LogoDark2x from "../images/logo-dark2x.png";
import {
  Block,
  BlockContent,
  BlockDes,
  BlockHead,
  BlockTitle,
  Button,
  Icon,
  PreviewCard,
} from "../components/Component";

import authService from "../auth/authService";
import { setTokens } from "../auth/tokenHelpers";
import { fetchCurrentUser } from "../store/slices/currentUserSlice";
import { fetchAccessibleModules } from "../store/slices/accessibleModulesSlice";
import type { AppDispatch } from "../store/store";

interface LoginCredentials {
  email: string;
  password: string;
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const [loading, setLoading] = useState<boolean>(false);
  const [passState, setPassState] = useState<boolean>(false);
  const [errorVal, setError] = useState<string>("");

  const redirectTo =
    new URLSearchParams(location.search).get("redirect") || "/home";

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginCredentials>();

  const getBrowserInfo = () => {
    const userAgent = navigator.userAgent;
    const browserMapper: Record<string, string> = {
      Edg: "Microsoft Edge",
      OPR: "Opera",
      Chrome: "Chrome",
      Safari: "Safari",
      Firefox: "Firefox",
    };
    for (const key in browserMapper) {
      if (userAgent.indexOf(key) > -1) return browserMapper[key];
    }
    return "Unknown";
  };

  const getIpAddress = async (): Promise<string | undefined> => {
    try {
      const response = await axios.get("https://api.ipify.org?format=json");
      return response.data.ip;
    } catch {
      return undefined;
    }
  };

  const onFormSubmit = async (formData: LoginCredentials) => {
    setLoading(true);
    setError("");
    try {
      const data = await authService.login({
        ...formData,
        applicationType: "Buyer",
      });
      if (data.status && data.token && data.token.access_token) {
        setTokens(data.token);
        localStorage.setItem("loginMethod", "regular");

        // Load the signed-in user into Redux so the header (avatar,
        // name, role, org logo) has data on first render of /home.
        dispatch(fetchCurrentUser());
        // Load the modules this user can access — used to filter
        // the launcher cards on the Home page.
        dispatch(fetchAccessibleModules());

        // Best-effort: capture login activity (does not block navigation).
        getIpAddress().then((ip) => {
          // Browser info + IP available for downstream activity logging.
          // Backend endpoint can be wired in once available.
          void getBrowserInfo();
          void ip;
        });

        navigate(redirectTo, { replace: true });
      } else {
        setError(data.message || "Unable to login with credentials");
      }
      setLoading(false);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Login failed");
      setLoading(false);
    }
  };

  return (
    <>
      <Head title="Login" />
      <div className="login-container pt-2">
        <div className="brand-logo pb-1 text-center">
          <Link to={process.env.PUBLIC_URL + "/"} className="logo-link">
            <img className="" src={LogoDark2x} alt="logo" />
          </Link>
        </div>
        <div className="d-flex justify-content-center h-100">
          <div className="header-area title">
            Revolutionizing Construction Through
            <span style={{ color: "rgb(37, 99, 235)" }}> AI</span> :
            Streamlining
            <span style={{ color: "rgb(37, 99, 235)" }}>
              <ReactTyped
                strings={[`&nbsp; Procurement &nbsp;`]}
                typeSpeed={20}
                showCursor={false}
              />
            </span>
            Processes
          </div>
          <Block className="nk-block-middle nk-auth-body wide-xs w-50 pb-0">
            <PreviewCard
              className="login-previewCard card-bordered"
              bodyClass="card-inner-lg"
            >
              <BlockHead>
                <BlockContent>
                  <BlockTitle tag="h4">Sign-In</BlockTitle>
                  <BlockDes>
                    <p>
                      <ReactTyped
                        strings={[
                          `Access Procurex using your email and password.`,
                        ]}
                        typeSpeed={10}
                        showCursor={false}
                      />
                    </p>
                  </BlockDes>
                </BlockContent>
              </BlockHead>

              {errorVal && (
                <div className="mb-3">
                  <Alert color="danger" className="alert-icon">
                    <Icon name="alert-circle" />
                    {errorVal}
                  </Alert>
                </div>
              )}

              <Form className="is-alter" onSubmit={handleSubmit(onFormSubmit)}>
                <div className="form-group">
                  <div className="form-label-group">
                    <label className="form-label" htmlFor="default-01">
                      Email Address
                    </label>
                  </div>
                  <div className="form-control-wrap">
                    <input
                      type="text"
                      id="default-01"
                      {...register("email", {
                        required: "This field is required",
                      })}
                      placeholder="Enter your email address"
                      className="form-control-lg form-control"
                    />
                    {errors.email && (
                      <span className="invalid">{errors.email.message}</span>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <div className="form-label-group">
                    <label className="form-label" htmlFor="password">
                      Password
                    </label>
                  </div>
                  <div className="form-control-wrap">
                    <a
                      href="#password"
                      onClick={(ev) => {
                        ev.preventDefault();
                        setPassState(!passState);
                      }}
                      className={`form-icon lg form-icon-right passcode-switch ${
                        passState ? "is-hidden" : "is-shown"
                      }`}
                    >
                      <Icon name="eye" className="passcode-icon icon-show" />
                      <Icon name="eye-off" className="passcode-icon icon-hide" />
                    </a>
                    <input
                      type={passState ? "text" : "password"}
                      id="password"
                      {...register("password", {
                        required: "This field is required",
                      })}
                      placeholder="Enter your passcode"
                      className={`form-control-lg form-control ${
                        passState ? "is-hidden" : "is-shown"
                      }`}
                    />
                    {errors.password && (
                      <span className="invalid">{errors.password.message}</span>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <Button
                    size="lg"
                    className="btn-block"
                    type="submit"
                    color="primary"
                  >
                    {loading ? <Spinner size="sm" color="light" /> : "Sign in"}
                  </Button>
                </div>
              </Form>

              <div className="form-note-s2 text-center pt-4">
                Forgot Password?{" "}
                <Link className="link link-primary" to="/auth-reset">
                  Click here to reset
                </Link>
              </div>
            </PreviewCard>
          </Block>
        </div>
        <div className="login-bgi"></div>
      </div>
      <AuthFooter />
    </>
  );
};

export default Login;
